import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, PenTool, Type } from "lucide-react";
import { formatDateET } from "@/lib/dates";

export default function SignContractPage() {
  const [params] = useSearchParams();
  const contractId = params.get("id");
  const [contract, setContract] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [sigMode, setSigMode] = useState("typed");
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const { theme, setTheme } = useTheme();

  // Force LIGHT mode on this public page; restore admin's theme on unmount.
  useEffect(() => {
    const prev = theme;
    setTheme("light");
    return () => { if (prev && prev !== "light") setTheme(prev); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!contractId) { setLoading(false); return; }
    api.get(`/contracts/sign/${contractId}`)
      .then(({ data }) => { setContract(data.contract); setLeadName(data.lead_name); })
      .catch(() => toast.error("Contract not found"))
      .finally(() => setLoading(false));
  }, [contractId]);

  useEffect(() => {
    if (sigMode === "drawn" && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth;
      canvas.height = 150;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#18181b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, [sigMode]);

  const startDraw = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => setDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    let sigData = "";
    if (sigMode === "typed") {
      if (!typedName.trim()) { toast.error("Please type your full name"); return; }
      sigData = typedName;
    } else {
      sigData = canvasRef.current?.toDataURL("image/png") || "";
      if (!sigData || sigData.length < 1000) { toast.error("Please draw your signature"); return; }
    }
    setSubmitting(true);
    try {
      await api.post(`/contracts/sign/${contractId}`, { signature_type: sigMode, signature_data: sigData });
      setSigned(true);
      toast.success("Contract signed!");
    } catch (err) { toast.error(err.response?.data?.detail || "Signing failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-gray-400">Loading contract...</p></div>;
  if (!contract) return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-gray-500">Contract not found.</p></div>;

  if (signed || contract.status === "signed") {
    const isWaiver = contract.is_waiver;
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <Card className="w-full max-w-md text-center bg-white border-gray-200">
          <CardContent className="p-8 space-y-4">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h2 className="text-2xl font-bold text-gray-900">{isWaiver ? "Waiver" : "Contract"} Signed!</h2>
            <p className="text-gray-500">Thank you. A signed copy is on its way to your email.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8 px-4" data-testid="sign-contract-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Contract Agreement</h1>
          <p className="text-gray-500 text-sm mt-1">For: {leadName}</p>
        </div>

        <Card className="bg-white border-gray-200">
          <CardContent className="p-6 bg-white">
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800 max-h-96 overflow-y-auto border-b border-gray-200 pb-4 mb-4 bg-white">
              {contract.body}
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={sigMode === "typed" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSigMode("typed")}
                  data-testid="sig-typed-btn"
                >
                  <Type size={14} /> Type Name
                </Button>
                <Button
                  variant={sigMode === "drawn" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSigMode("drawn")}
                  data-testid="sig-draw-btn"
                >
                  <PenTool size={14} /> Draw Signature
                </Button>
              </div>

              {sigMode === "typed" ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Type your full legal name</label>
                  <Input
                    data-testid="sig-typed-input"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Full Name"
                    className="text-lg font-serif italic"
                  />
                  {typedName && (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-serif italic text-gray-800">{typedName}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDateET(new Date())}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Draw your signature below</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    <canvas
                      ref={canvasRef}
                      data-testid="sig-canvas"
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={startDraw}
                      onMouseMove={draw}
                      onMouseUp={stopDraw}
                      onMouseLeave={stopDraw}
                      onTouchStart={startDraw}
                      onTouchMove={draw}
                      onTouchEnd={stopDraw}
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearCanvas} data-testid="sig-clear">Clear</Button>
                </div>
              )}

              <Button
                data-testid="sign-contract-btn"
                className="w-full"
                onClick={handleSign}
                disabled={submitting}
              >
                {submitting ? "Signing..." : "Sign Contract"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
